import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import {
  getStockItem,
  updateStockItem,
  type StockItem,
  type StockItemUpdatePayload,
} from "@/api/stock";
import { ApiError } from "@/api/client";
import { useProduktbilder, useResolvedImages } from "@/api/produktbilder";
import { ProductPhoto } from "@/components/ProductPhoto";
import { Panel } from "@/components/Panel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EditableField } from "@/components/EditableField";
import { ArtikelBewegungen } from "./ArtikelBewegungen";
import { ArtikelLagerplaetze } from "@/pages/warehouse/components/ArtikelLagerplaetze";
import { CharacteristicsTab } from "./tabs/CharacteristicsTab";
import { KeywordsTab } from "./tabs/KeywordsTab";
import { FaqsTab } from "./tabs/FaqsTab";
import { ChannelsTab } from "./tabs/ChannelsTab";
import { CrossSellingTab } from "./tabs/CrossSellingTab";
import VerkaufslinksTab from "./tabs/VerkaufslinksTab";
import { ShopifyListingBlock } from "./tabs/ShopifyListingBlock";
import ProduktWissenTab from "./tabs/ProduktWissenTab";
import { MarginSection } from "./tabs/MarginSection";
import { ZaehlweisePanel } from "./tabs/ZaehlweisePanel";
import { VersandregelPanel } from "./tabs/VersandregelPanel";
import { PreisRolloutBlock } from "./tabs/rollout/PreisRolloutBlock";
import { RolloutKopfStatistik } from "./tabs/rollout/RolloutKopfStatistik";
import { URL_PARAM as ROLLOUT_URL_PARAM, type SichtPortal } from "./tabs/rollout/rolloutView";
import { BestandMitVorab } from "@/components/vorab/BestandMitVorab";
import { KatalogSuche } from "./KatalogSuche";
import { AusverkaufSchalter } from "./AusverkaufSchalter";
import {
  usePictures,
  useKeywords,
  useFaqs,
  useCharacteristics,
} from "@/api/stockSubresources";

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(+d)) return s;
  return d.toLocaleString("de-DE");
}

interface CompletenessSubs {
  picturesCount: number;
  keywordsCount: number;
  faqsCount: number;
  characteristicsCount: number;
}

function completeness(item: StockItem, subs: CompletenessSubs): number {
  const checks = [
    !!item.barcode,
    !!item.mpn,
    !!item.manufacturer,
    !!item.merchant_sku,
    Number(item.price) > 0,
    Number(item.supplier_price ?? 0) > 0,
    item.quantity_alert !== undefined && item.quantity_alert > 0,
    !!item.weight_grams,
    !!item.customs_hs_code,
    !!item.customs_origin_country,
    !!item.content_quantity,
    !!item.content_type,
    subs.picturesCount > 0,
    subs.keywordsCount >= 8,
    subs.faqsCount > 0,
    subs.characteristicsCount > 0,
  ];
  const filled = checks.filter(Boolean).length;
  return Math.round((filled / checks.length) * 100);
}

/** weight_grams enthaelt Milligramm (CLAUDE.md §3); die UI arbeitet in Gramm. */
function gramsToDb(g: number | null): number | null {
  return g == null ? null : Math.round(g * 1000);
}

type SaveFn = <K extends keyof StockItemUpdatePayload>(
  key: K,
  value: StockItemUpdatePayload[K],
) => Promise<void>;

/** Selten gebrauchte DR-Altfelder, Freitexte, Cross-Selling und Rohdaten — zugeklappt,
 *  damit der Alltag nur die Felder sieht, die er pflegt. Inhalt wird erst beim
 *  Aufklappen gerendert (Cross-Selling laedt sonst unnoetig Daten). */
function TechnikSection({ item, save }: { item: StockItem; save: SaveFn }) {
  const [open, setOpen] = useState(false);
  const Icon = open ? ChevronDown : ChevronRight;
  return (
    <section className="rounded-md border border-line bg-panel">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-5 py-3 text-left"
      >
        <Icon className="size-4 text-muted" aria-hidden />
        <span className="font-display font-semibold text-base text-ink">Technik</span>
        <span className="text-xs text-ink-2">DR-Felder, Freitexte, Cross-Selling, Rohdaten</span>
      </button>
      {open && (
        <div className="border-t border-line-2 p-5 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="DR-Zuordnung">
              <div className="flex flex-col gap-3">
                <EditableField
                  type="text"
                  mono
                  label="SKU (DR-intern)"
                  value={item.sku}
                  onSave={() => Promise.resolve()}
                  disabled
                />
                <EditableField
                  type="text"
                  mono
                  label="DR Lagernummer"
                  value={item.dr_stock_id}
                  onSave={() => Promise.resolve()}
                  disabled
                  hint="Wird vom DR-Sync gepflegt"
                />
                <EditableField
                  type="text"
                  mono
                  label="DR SKU"
                  value={item.dr_sku}
                  onSave={() => Promise.resolve()}
                  disabled
                />
                <EditableField
                  type="text"
                  mono
                  label="DR Lieferanten-ID"
                  value={item.dr_supplier_id}
                  onSave={(v) => save("dr_supplier_id", v as string)}
                />
                <EditableField
                  type="number"
                  mono
                  label="Bestandsquelle (ID)"
                  hint="Bestandsabzug von Grundartikel"
                  value={item.stock_source_id}
                  onSave={() => Promise.resolve()}
                  disabled
                />
                <EditableField
                  type="text"
                  mono
                  label="Bestandsquelle (SKU)"
                  value={item.stock_source_sku}
                  onSave={() => Promise.resolve()}
                  disabled
                />
                <EditableField
                  type="number"
                  mono
                  label="Ordner-ID"
                  value={item.folder_id}
                  onSave={(v) => save("folder_id", v as number)}
                />
                <EditableField
                  type="number"
                  mono
                  label="Kategorie-ID"
                  value={item.category_id}
                  onSave={(v) => save("category_id", v as number)}
                />
                <EditableField
                  type="number"
                  mono
                  label="Zustand-ID"
                  value={item.condition_id}
                  onSave={(v) => save("condition_id", v as number)}
                />
                <EditableField
                  type="text"
                  mono
                  label="Alarm-Status"
                  value={item.alarm_state}
                  onSave={() => Promise.resolve()}
                  disabled
                />
              </div>
            </Panel>

            <Panel title="Infos & Freitexte">
              <div className="flex flex-col gap-3">
                <EditableField
                  type="textarea"
                  label="Infos"
                  value={item.infos}
                  onSave={(v) => save("infos", v as string)}
                />
                <EditableField
                  type="textarea"
                  label="Versand-Infos"
                  value={item.shipping_infos}
                  onSave={(v) => save("shipping_infos", v as string)}
                />
                <EditableField
                  type="text"
                  label="Freitext 1"
                  value={item.freetext_1}
                  onSave={(v) => save("freetext_1", v as string)}
                />
                <EditableField
                  type="text"
                  label="Freitext 2"
                  value={item.freetext_2}
                  onSave={(v) => save("freetext_2", v as string)}
                />
                <EditableField
                  type="text"
                  label="Freitext 3"
                  value={item.freetext_3}
                  onSave={(v) => save("freetext_3", v as string)}
                />
                <EditableField
                  type="text"
                  label="Freitext 4"
                  value={item.freetext_4}
                  onSave={(v) => save("freetext_4", v as string)}
                />
                <EditableField
                  type="text"
                  label="Freitext 5"
                  value={item.freetext_5}
                  onSave={(v) => save("freetext_5", v as string)}
                />
              </div>
            </Panel>
          </div>

          <CrossSellingTab sku={item.sku} item={item} save={save} />

          <Panel title="Alle Felder" subtitle={`${Object.keys(item).length} Felder, nur lesen`}>
            <pre className="font-mono text-[11px] leading-relaxed text-ink-2 overflow-auto max-h-[600px] bg-panel-2 p-4 rounded border border-line">
              {JSON.stringify(item, null, 2)}
            </pre>
            <div className="mt-2 flex items-center gap-3 text-xs text-muted">
              <span>Erstellt: {fmtDate(item.created_at)}</span>
              <span>·</span>
              <span>Geändert: {fmtDate(item.updated_at)}</span>
            </div>
          </Panel>
        </div>
      )}
    </section>
  );
}

// Rueckweg zum Katalog: Adresse, von der aus der Artikel geoeffnet wurde (Ordner, Suche).
// Nur eine lokale Adresse mit genau dem Pfad /catalog zaehlt, sonst die Uebersicht.
function katalogHerkunft(state: unknown): string {
  const liste = (state as { liste?: unknown } | null)?.liste;
  if (typeof liste !== "string") return "/catalog";
  return liste.split(/[?#]/)[0] === "/catalog" ? liste : "/catalog";
}

const DETAIL_TABS = ["artikel", "preise", "rollout", "lager", "verkauf", "wissen"] as const;

export default function GrundartikelDetail() {
  const { sku } = useParams<{ sku: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Reiter in der Adresse (?tab=), damit Filter-Links des Preis-Rollout-Blocks den Reiter mitnehmen.
  // `state` bleibt erhalten — sonst verloere „Produktkatalog“ die Katalog-Herkunft.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab = tabParam && (DETAIL_TABS as readonly string[]).includes(tabParam) ? tabParam : "artikel";
  const setTab = (next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next === "artikel") p.delete("tab");
    else p.set("tab", next);
    setSearchParams(p, { replace: true, state: location.state });
  };
  // Kopf-Statistik: Reiter Preis-Rollout, mit Portal gefiltert auf offene Varianten dieses Portals.
  const oeffneRollout = (offenIn?: SichtPortal) => {
    const p = new URLSearchParams(searchParams);
    p.set("tab", "rollout");
    if (offenIn) {
      p.set(ROLLOUT_URL_PARAM.rollout, "offen");
      p.set(ROLLOUT_URL_PARAM.portale, offenIn);
    }
    setSearchParams(p, { replace: true, state: location.state });
  };
  const queryClient = useQueryClient();
  const { data: produktbilder = [] } = useProduktbilder(sku);
  const hauptbild = produktbilder.find(bild => bild.is_primary);
  const resolved = useResolvedImages([sku]);
  const [failedImage, setFailedImage] = useState<string | null>(null);

  const { data: item, isLoading, isError, error } = useQuery({
    queryKey: ["grundartikel", sku],
    queryFn: () => getStockItem(sku!),
    enabled: !!sku,
  });

  // Ziel-SKU reist mit der Mutation: endet ein Speichern erst nach dem Wechsel zu einem anderen
  // Artikel, landet das Ergebnis trotzdem im Cache des gespeicherten Artikels.
  const mutation = useMutation({
    mutationFn: ({ ziel, payload }: { ziel: string; payload: StockItemUpdatePayload }) =>
      updateStockItem(ziel, payload),
    onSuccess: (updated, { ziel }) => {
      queryClient.setQueryData(["grundartikel", ziel], updated);
      void queryClient.invalidateQueries({ queryKey: ["catalog-grundartikel"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory-stock"] });
      toast.success("Gespeichert");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    },
  });

  async function save<K extends keyof StockItemUpdatePayload>(
    key: K,
    value: StockItemUpdatePayload[K],
  ) {
    await mutation.mutateAsync({ ziel: sku!, payload: { [key]: value } as StockItemUpdatePayload });
  }

  // Sub-Resources fuer Vollstaendigkeit + Tab-Counter
  const { data: pics = [] } = usePictures(sku);
  const { data: kws = [] } = useKeywords(sku);
  const { data: faqs = [] } = useFaqs(sku);
  const { data: chars = [] } = useCharacteristics(sku);

  const pct = useMemo(
    () =>
      item
        ? completeness(item, {
            picturesCount: pics.length,
            keywordsCount: kws.length,
            faqsCount: faqs.length,
            characteristicsCount: chars.length,
          })
        : 0,
    [item, pics.length, kws.length, faqs.length, chars.length],
  );

  // Die Leiste bricht in schmalen Bereichen um (Suche in eigener Zeile) – ihre Hoehe wird gemessen
  // und als --leiste-h an Reiter (sticky top) und Sprungziele (scroll-pt) weitergegeben.
  const bereichRef = useRef<HTMLDivElement>(null);
  const leisteRef = useRef<HTMLDivElement>(null);
  const titelRef = useRef<HTMLHeadingElement>(null);
  const [leisteHoehe, setLeisteHoehe] = useState<number | null>(null);
  const [kompakt, setKompakt] = useState(false);
  const geladen = !!item;
  useEffect(() => {
    const leiste = leisteRef.current;
    if (!leiste || typeof ResizeObserver === "undefined") return;
    const beobachter = new ResizeObserver(([eintrag]) => {
      const hoehe = eintrag!.borderBoxSize?.[0]?.blockSize ?? eintrag!.target.getBoundingClientRect().height;
      setLeisteHoehe(Math.round(hoehe));
    });
    beobachter.observe(leiste);
    return () => beobachter.disconnect();
  }, [geladen]);

  // Kompakter Kopf: sobald der Titel unter der Leiste verschwindet, zeigt die Leiste Sternnummer,
  // Titel und Bestand. Ihre Hoehe bleibt dabei gleich – kein Springen beim Umschalten.
  useEffect(() => {
    const titel = titelRef.current;
    if (!titel || typeof IntersectionObserver === "undefined") return;
    const beobachter = new IntersectionObserver(
      // Mehrere Einträge je Aufruf möglich (gedrosselter Tab, schneller Scroll): der letzte ist aktuell.
      (eintraege) => setKompakt(!eintraege[eintraege.length - 1]!.isIntersecting),
      { root: bereichRef.current, rootMargin: `-${leisteHoehe ?? 48}px 0px 0px 0px` },
    );
    beobachter.observe(titel);
    return () => beobachter.disconnect();
    // sku: der Inhalt wird je Artikel neu gemountet (key) – neuer Titel-Knoten.
  }, [geladen, sku, leisteHoehe]);

  // Neuer Artikel (z. B. aus der Suche) beginnt oben.
  useEffect(() => {
    if (bereichRef.current) bereichRef.current.scrollTop = 0;
  }, [sku]);

  if (isLoading) {
    return <div className="p-8 text-sm text-ink-2">Lade Artikel…</div>;
  }
  if (!item) {
    return (
      <div className="p-8 space-y-3">
        <p className="text-sm text-coral">
          {error instanceof ApiError ? error.message : "Artikel nicht gefunden"}
        </p>
        <button
          onClick={() => navigate(katalogHerkunft(location.state))}
          className="text-sm text-primary hover:underline"
        >
          ← Zurück
        </button>
      </div>
    );
  }

  const barColor = pct >= 75 ? "bg-success" : pct >= 50 ? "bg-warn" : "bg-coral";

  return (
    // Eigener Scrollbereich (Höhe = Fenster minus TopBar h-14): main hat overflow-auto ohne Höhe,
    // dort greift sticky nicht. So bleiben Leiste und Reiter beim Scrollen oben stehen;
    // scroll-pt hält Sprungziele unter beiden frei (Leiste + ca. 3,5rem Reiter).
    // @container: Umbruch nach der Breite des Inhaltsbereichs, nicht des Fensters (Seitenleiste).
    <div
      ref={bereichRef}
      data-testid="artikel-scrollbereich"
      style={leisteHoehe ? ({ "--leiste-h": `${leisteHoehe}px` } as CSSProperties) : undefined}
      className="@container h-[calc(100dvh-3.5rem)] overflow-y-auto scroll-pt-[calc(var(--leiste-h,3rem)+3.5rem)]"
    >
      {isError && <p role="status" className="border-b border-line bg-warn-soft px-6 py-2 text-sm text-warn">
        Artikel konnte nicht neu geladen werden. Angezeigt wird der zuletzt bestätigte Stand.
      </p>}
      <div
        ref={leisteRef}
        data-testid="zurueck-leiste"
        data-kompakt={String(kompakt)}
        className="sticky top-0 z-20 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line bg-background/95 px-6 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80 @3xl:min-h-12 @3xl:py-1.5"
      >
        <button
          onClick={() => navigate(katalogHerkunft(location.state))}
          className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-1 -ml-1.5 text-sm text-ink-2 hover:bg-panel-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-primary"
        >
          <ChevronLeft className="size-4" />
          {/* Schmal und kompakt: Platz für Sternnummer und Titel, der Name bleibt für Screenreader. */}
          <span className={kompakt ? "@max-3xl:sr-only" : undefined}>Produktkatalog</span>
        </button>
        {/* Mindestbreite immer (auch nicht kompakt): reicht der Platz neben der Suche nicht, bricht die Suche
            in die nächste Zeile (flex-wrap) statt Titel und Bestand zu überlagern – die Leistenhöhe
            ändert sich beim Umschalten nicht (sonst springt der Inhalt und der Beobachter flackert). */}
        <div
          data-testid="kompakt-identitaet"
          className="flex min-h-8 min-w-0 flex-[999_1_0%] items-center gap-2.5 @3xl:min-w-[22rem]"
        >
          {kompakt && (
            <>
              <span className="h-4 w-px shrink-0 self-center bg-line" aria-hidden />
              <span className="shrink-0 font-mono text-base font-bold text-primary motion-safe:animate-in motion-safe:fade-in">
                <GrundartikelLink code={item.merchant_sku ?? item.sku} state={location.state} />
              </span>
              <span className="min-w-0 flex-1 truncate font-display text-sm font-semibold text-ink motion-safe:animate-in motion-safe:fade-in">
                {item.name}
              </span>
              {/* Gleiche Rechnung wie der grosse Kopf: Kind/Set nur DR, sonst DR + offene Vorab-Stück. */}
              <span data-testid="kompakt-bestand" className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-ink">
                <span className="text-[10px] uppercase tracking-wider text-muted @max-3xl:hidden">Bestand</span>
                <BestandMitVorab
                  variante="kurz"
                  quantity={item.quantity}
                  vorabOffen={item.vorab_offen}
                  vorabEintraege={item.vorab_eintraege}
                  nurDr={item.stock_source_id != null}
                />
              </span>
            </>
          )}
        </div>
        {/* grow 1 gegen 999 der Identität: neben dem Titel bleibt die Suche ~basis breit; allein in
            einer Zeile füllt sie diese, ihr rechter Rand (Anker der Trefferliste) fluchtet mit dem Inhalt. */}
        <KatalogSuche className="w-full @3xl:w-auto @3xl:grow @3xl:basis-64 @5xl:basis-80" />
      </div>
    {/* key: Wechsel zu einem (gecachten) anderen Artikel mountet Felder neu – offene Entwürfe von A
        landen nie auf B. */}
    <div key={sku} className="px-6 pb-6 pt-4 space-y-4">

      {/* Immer umbruchfähig: basis-64 am Titelblock schiebt den Bestand (mit breiter Vorab-Herkunft)
          in die nächste Zeile, statt den Titel auf wenige Pixel zu quetschen – auch neben der Seitenleiste. */}
      <header className="flex flex-wrap items-start gap-5">
        <div className="min-h-24 min-w-24 max-w-md shrink-0 rounded-md border border-line-2 bg-panel-2 grid place-items-center text-muted-2">
          {resolved.data?.enabled !== false ? <div className={resolved.data?.results[0]?.components.length ? 'p-2' : 'size-24'}><ProductPhoto images={resolved.data?.results[0]} alt={`Hauptbild: ${item.name}`} /></div> : hauptbild?.public_url && hauptbild.public_url !== failedImage ? (
            <img src={hauptbild.public_url} alt={`Hauptbild: ${item.name}`} className="size-full rounded-md object-contain"
              onError={() => setFailedImage(hauptbild.public_url)} />
          ) : <div className="flex flex-col items-center gap-1">
            <ImageIcon className="size-8" />
            <span className="text-[10px] uppercase">Bild</span>
          </div>}
        </div>

        <div className="min-w-0 flex-1 basis-64">
          <h1 ref={titelRef} className="break-words font-display font-bold text-2xl text-ink">{item.name}</h1>
          <AusverkaufSchalter key={item.sku} item={item} />
          {/* Sternnummer vorn und groß – danach wird im Lager gesucht; DR-Nr. nur klein rechts. */}
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-ink-2 mt-1">
            {item.merchant_sku ? (
              <span className="font-mono text-xl font-bold leading-none text-primary" title="Händler-Artikelnummer (Sternnummer)">
                <GrundartikelLink code={item.merchant_sku} state={location.state} />
              </span>
            ) : (
              <span className="font-mono text-xl font-bold leading-none text-ink" title="DreamRobot-Nummer des Grundartikels">
                {item.sku}
              </span>
            )}
            <span className="text-muted">·</span>
            <span>{item.manufacturer ?? "—"}</span>
            <span className="text-muted">·</span>
            <span className="font-mono text-xs">EAN {item.barcode ?? "—"}</span>
            {item.merchant_sku && (
              <span className="ml-auto font-mono text-[11px] text-muted" title="DreamRobot-Nummer des Grundartikels">
                {item.sku}
              </span>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3 max-w-md">
            <span className="text-[10px] uppercase tracking-wider text-muted font-semibold w-full sm:w-32">
              Vollständigkeit
            </span>
            <div className="flex-1 h-1.5 bg-panel-2 rounded">
              <div
                className={`h-full rounded ${barColor}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="font-mono text-sm text-ink font-semibold">{pct}%</span>
          </div>
        </div>

        <div className="ml-auto flex max-w-full flex-wrap justify-end gap-5">
          <div data-testid="rollout-kopf-platz" className="order-2 min-h-40 w-80 max-w-full @3xl:order-1">
            <RolloutKopfStatistik sku={item.sku} onOeffne={oeffneRollout} />
          </div>
          <div className="order-1 max-w-full shrink-0 text-right @3xl:order-2">
          <div className="text-[10px] uppercase tracking-wider text-muted font-semibold">
            Bestand
          </div>
          {item.stock_source_id != null || !item.vorab_offen ? (
            <div className="font-display font-bold text-2xl font-mono text-ink">
              {item.quantity.toLocaleString("de-DE")}
              {item.stock_source_id != null && <span className="ml-1 text-xs text-ink-2">DR</span>}
            </div>
          ) : (
            <BestandMitVorab variante="kopf" quantity={item.quantity} vorabOffen={item.vorab_offen} vorabEintraege={item.vorab_eintraege} />
          )}
          {item.quantity_reserved > 0 && (
            <div className="text-[11px] text-ink-2">
              davon {item.quantity_reserved.toLocaleString("de-DE")} reserviert
            </div>
          )}
          </div>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        {/* Reiter haften direkt unter der Leiste (gemessene Höhe, vor der Messung 3rem). */}
        <div
          data-testid="reiter-leiste"
          className={`sticky top-[var(--leiste-h,3rem)] z-10 -mx-6 px-6 py-2 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b ${kompakt ? "border-line" : "border-transparent"}`}
        >
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="artikel">Artikel</TabsTrigger>
            <TabsTrigger value="preise">Preise</TabsTrigger>
            <TabsTrigger value="rollout">Preis-Rollout</TabsTrigger>
            <TabsTrigger value="lager">Lager</TabsTrigger>
            <TabsTrigger value="verkauf">Verkauf</TabsTrigger>
            <TabsTrigger value="wissen">Produkt-Wissen</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="artikel" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Identifikation">
              <div className="flex flex-col gap-3">
                <EditableField
                  type="text"
                  label="Name"
                  value={item.name}
                  onSave={(v) => save("name", v as string)}
                />
                <EditableField
                  type="text"
                  mono
                  label="Händler-Artikelnummer"
                  hint="Stahlia-Format: *NNN"
                  value={item.merchant_sku}
                  onSave={(v) => save("merchant_sku", v as string)}
                />
                <EditableField
                  type="text"
                  label="Hersteller"
                  value={item.manufacturer}
                  onSave={(v) => save("manufacturer", v as string)}
                />
                <EditableField
                  type="text"
                  mono
                  label="Barcode (EAN/UPC)"
                  value={item.barcode}
                  onSave={(v) => save("barcode", v as string)}
                />
                <EditableField
                  type="text"
                  mono
                  label="MPN"
                  value={item.mpn}
                  onSave={(v) => save("mpn", v as string)}
                />
              </div>
            </Panel>

            <ZaehlweisePanel stockItemId={item.id} />

            <VersandregelPanel
              stockItemId={item.id}
              sku={item.sku}
              laengsteSeiteMm={Math.max(item.size_length_mm ?? 0, item.size_width_mm ?? 0, item.size_height_mm ?? 0) || null}
            />

            <Panel title="Maße & Gewicht">
              <div className="flex flex-col gap-3">
                <EditableField
                  type="number"
                  mono
                  step="0.1"
                  label="Gewicht (g)"
                  value={item.weight_grams != null ? item.weight_grams / 1000 : null}
                  onSave={(v) => save("weight_grams", gramsToDb(v as number | null))}
                />
                <EditableField
                  type="number"
                  mono
                  label="Länge (mm)"
                  value={item.size_length_mm}
                  onSave={(v) => save("size_length_mm", v as number)}
                />
                <EditableField
                  type="number"
                  mono
                  label="Breite (mm)"
                  value={item.size_width_mm}
                  onSave={(v) => save("size_width_mm", v as number)}
                />
                <EditableField
                  type="number"
                  mono
                  label="Höhe (mm)"
                  value={item.size_height_mm}
                  onSave={(v) => save("size_height_mm", v as number)}
                />
                <EditableField
                  type="boolean"
                  label="Fulfillment (FBA / extern)"
                  value={item.is_fulfillment ?? false}
                  onSave={(v) => save("is_fulfillment", v as boolean)}
                />
              </div>
            </Panel>

            <Panel title="Zoll & Lieferant">
              <div className="flex flex-col gap-3">
                <EditableField
                  type="text"
                  label="Zoll-Beschreibung"
                  value={item.customs_description}
                  onSave={(v) => save("customs_description", v as string)}
                />
                <EditableField
                  type="text"
                  mono
                  label="HS-Code"
                  value={item.customs_hs_code}
                  onSave={(v) => save("customs_hs_code", v as string)}
                />
                <EditableField
                  type="text"
                  mono
                  label="Ursprungsland (ISO, z.B. DE)"
                  value={item.customs_origin_country}
                  onSave={(v) => save("customs_origin_country", v as string)}
                />
                <EditableField
                  type="number"
                  mono
                  label="Lieferant (Nr.)"
                  value={item.supplier_id}
                  onSave={(v) => save("supplier_id", v as number)}
                />
              </div>
            </Panel>
          </div>

          <TechnikSection item={item} save={save} />
        </TabsContent>

        <TabsContent value="preise" className="space-y-4">
          <Panel title="Einkauf & Verkaufspreis">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3">
              <EditableField
                type="number"
                mono
                step="0.01"
                label="Einkaufspreis (EK)"
                value={item.supplier_price ? Number(item.supplier_price) : null}
                onSave={(v) => save("supplier_price", v as number)}
              />
              <EditableField
                type="number"
                mono
                step="0.01"
                label="Verkaufspreis (VK)"
                value={item.price ? Number(item.price) : null}
                onSave={(v) => save("price", v as number)}
              />
              <EditableField
                type="number"
                mono
                step="0.01"
                label="MwSt. (Faktor, z.B. 0.19)"
                value={item.tax ? Number(item.tax) : null}
                onSave={(v) => save("tax", v as number)}
              />
              <EditableField
                type="number"
                mono
                step="0.01"
                label="UVP"
                value={item.rrp ? Number(item.rrp) : null}
                onSave={(v) => save("rrp", v as number)}
              />
              <EditableField
                type="number"
                mono
                label="Inhalt Menge"
                value={item.content_quantity}
                onSave={(v) => save("content_quantity", v as number)}
              />
              <EditableField
                type="text"
                label="Inhalt Typ (z.B. ml, kg, stk)"
                value={item.content_type}
                onSave={(v) => save("content_type", v as string)}
              />
              <EditableField
                type="text"
                label="Grundpreis-Typ"
                value={item.content_basic_price_type}
                onSave={(v) => save("content_basic_price_type", v as string)}
              />
            </div>
          </Panel>
          <MarginSection sku={item.sku} />
        </TabsContent>

        <TabsContent value="rollout" className="space-y-4">
          <PreisRolloutBlock sku={item.sku} />
        </TabsContent>

        <TabsContent value="verkauf" className="space-y-4">
          {import.meta.env.DEV && <ShopifyListingBlock sku={item.sku} />}
          <Panel title="Verkaufs-Angebote (aktive Portale)">
            <VerkaufslinksTab sku={item.sku} />
          </Panel>
          <ChannelsTab sku={item.sku} />
        </TabsContent>

        <TabsContent value="lager" className="space-y-4">
          <Panel title="Bestand" subtitle="Bestand ändert sich über Wareneingang, Inventur und Umbuchungen.">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3">
              <EditableField
                type="number"
                mono
                label="Bestand"
                value={item.quantity}
                onSave={() => Promise.resolve()}
                disabled
              />
              <EditableField
                type="number"
                mono
                label="Reserviert"
                value={item.quantity_reserved}
                onSave={() => Promise.resolve()}
                disabled
              />
              <EditableField
                type="number"
                mono
                label="Meldebestand"
                value={item.quantity_alert}
                onSave={(v) => save("quantity_alert", v as number)}
              />
              <EditableField
                type="number"
                mono
                label="Mindestbestand"
                value={item.quantity_min}
                onSave={(v) => save("quantity_min", v as number)}
              />
              <EditableField
                type="number"
                mono
                label="Bestellt"
                value={item.quantity_ordered}
                onSave={() => Promise.resolve()}
                disabled
              />
              <EditableField
                type="text"
                mono
                label="Bestellt am"
                value={item.quantity_ordered_date}
                onSave={() => Promise.resolve()}
                disabled
              />
            </div>
          </Panel>
          {item.merchant_sku ? (
            <ArtikelLagerplaetze merchantSku={item.merchant_sku} />
          ) : (
            <p className="text-sm text-muted p-2">
              Keine Händler-Artikelnummer hinterlegt — Lagerplätze können nicht zugeordnet werden.
            </p>
          )}
          <ArtikelBewegungen key={item.sku} sku={item.sku} sternnummer={item.merchant_sku ?? null} />
        </TabsContent>


        <TabsContent value="wissen" className="space-y-4">
          <nav aria-label="Abschnitte" className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {[
              ["inhalt-keywords", `Keywords (${kws.length})`],
              ["inhalt-merkmale", `Merkmale (${chars.length})`],
              ["inhalt-faqs", `FAQs (${faqs.length})`],
              ["inhalt-bilder", `Bilder (${pics.length})`],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })}
                className="text-primary hover:underline"
              >
                {label}
              </button>
            ))}
          </nav>
          <ProduktWissenTab
            artikelname={item.name}
            sku={item.sku}
          >
            <div id="inhalt-keywords"><KeywordsTab sku={item.sku} /></div>
            <div id="inhalt-merkmale"><CharacteristicsTab sku={item.sku} /></div>
            <div id="inhalt-faqs"><FaqsTab sku={item.sku} /></div>
          </ProduktWissenTab>
        </TabsContent>
      </Tabs>
    </div>
    </div>
  );
}
